import React, { useState, useMemo, useEffect } from 'react';
import { Eye, EyeOff, Search, MapPin, Trash2, AlertTriangle } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
    updateCustomMapDetails,
    removeLocationFromMap,
    deleteCustomMap,
    selectMyMapById,
    selectPublicMapById,
    selectCustomMapOperationLoading
} from '@/store/slices/customMapsSlice';
import { fetchActiveLocationData } from '@/store/slices/activeLocationSlice';
import { selectAllLocations } from '@/store/slices/locationsSlice';
import { setActiveInfoWindowId } from '@/store/slices/mapUiSlice';

interface MapEditModalProps {
    mapId: string | null;
    isOpen: boolean;
    onClose: () => void;
}

const MapEditModal: React.FC<MapEditModalProps> = ({ mapId, isOpen, onClose }) => {
    const dispatch = useAppDispatch();
    
    // Get map details from store
    const myMap = useAppSelector(state => mapId ? selectMyMapById(state, mapId) : null);
    const publicMap = useAppSelector(state => mapId ? selectPublicMapById(state, mapId) : null);
    const map = myMap || publicMap;
    
    const allLocations = useAppSelector(selectAllLocations);
    const operationLoading = useAppSelector(selectCustomMapOperationLoading);

    // Local state
    const [mapName, setMapName] = useState('');
    const [description, setDescription] = useState('');
    const [isPublic, setIsPublic] = useState(false);
    const [locationFilter, setLocationFilter] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [savingDescription, setSavingDescription] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Initialize form with map data
    useEffect(() => {
        if (map) {
            setMapName(map.name);
            setDescription(map.description || '');
            setIsPublic(map.isPublic);
        }
    }, [map]);

    // Filter locations by name and get their details
    const mapLocations = useMemo(() => {
        if (!map) return [];
        
        return map.locationIds
            .map(id => allLocations.find(loc => loc.id === id))
            .filter(loc => loc !== undefined)
            .filter(loc => 
                loc!.name.toLowerCase().includes(locationFilter.toLowerCase()) ||
                loc!.address.toLowerCase().includes(locationFilter.toLowerCase())
            ) as any[];
    }, [map, allLocations, locationFilter]);

    if (!map || !mapId) return null;

    const handleSaveName = async () => {
        if (mapId && mapName.trim() !== map.name) {
            setSavingName(true);
            await dispatch(updateCustomMapDetails({ 
                mapId, 
                name: mapName.trim() 
            }));
            setSavingName(false);
        }
    };

    const handleSaveDescription = async () => {
        if (mapId && description !== (map.description || '')) {
            setSavingDescription(true);
            await dispatch(updateCustomMapDetails({ 
                mapId, 
                description: description.trim() 
            }));
            setSavingDescription(false);
        }
    };

    const handleTogglePublic = async () => {
        if (mapId) {
            await dispatch(updateCustomMapDetails({ 
                mapId, 
                isPublic: !isPublic 
            }));
            setIsPublic(!isPublic);
        }
    };

    const handleRemoveLocation = async (locationId: string) => {
        if (mapId) {
            await dispatch(removeLocationFromMap({ 
                mapId, 
                locationId 
            }));
        }
    };

    const handleGoToLocation = (locationId: string) => {
        dispatch(fetchActiveLocationData(locationId));
        dispatch(setActiveInfoWindowId(locationId));
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSaveName();
        }
    };

    const handleDeleteMap = async () => {
        if (mapId) {
            setDeleting(true);
            await dispatch(deleteCustomMap(mapId));
            setDeleting(false);
            onClose();
        }
    };

    return (
        <>
            <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
                <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col">
                    <DialogHeader className="flex flex-row items-center justify-between">
                        <DialogTitle>Edit Map</DialogTitle>
                        <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2 mr-7"
                            onClick={handleTogglePublic}
                        >
                            {isPublic ? (
                                <>
                                    <Eye className="h-4 w-4" />
                                    <span>Public</span>
                                </>
                            ) : (
                                <>
                                    <EyeOff className="h-4 w-4" />
                                    <span>Private</span>
                                </>
                            )}
                        </Button>
                    </DialogHeader>
                    
                    <div className="flex flex-col gap-4 overflow-hidden">
                        {/* Map Name Input */}
                        <div className="flex items-center gap-2">
                            <div className="flex-1">
                                <Label htmlFor="mapName" className="my-2">Map Name</Label>
                                <Input 
                                    id="mapName" 
                                    value={mapName} 
                                    onChange={e => setMapName(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                            <Button 
                                onClick={handleSaveName} 
                                disabled={mapName.trim() === map.name || mapName.trim() === '' || savingName}
                                className="mt-7"
                            >
                                Save
                            </Button>
                        </div>
                        
                        {/* Map Description Input */}
                        <div className="flex items-start gap-2">
                            <div className="flex-1">
                                <Label htmlFor="description" className="my-2">Description (Optional)</Label>
                                <Textarea 
                                    id="description" 
                                    value={description} 
                                    onChange={e => setDescription(e.target.value)}
                                    placeholder="Add a description for your map"
                                    className="resize-none h-20"
                                />
                            </div>
                            <Button 
                                onClick={handleSaveDescription} 
                                disabled={description === (map.description || '') || savingDescription}
                                className="mt-7"
                            >
                                Save
                            </Button>
                        </div>
                        
                        {/* Locations List */}
                        <div className="flex flex-col gap-2">
                            <Label>Locations in this map ({map.locationIds.length})</Label>
                            
                            <div className="relative">
                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Filter locations..."
                                    value={locationFilter}
                                    onChange={e => setLocationFilter(e.target.value)}
                                    className="pl-8"
                                />
                            </div>
                            
                            <div className="border rounded-md h-[210px] overflow-y-auto">
                                {mapLocations.length > 0 ? (
                                    <div className="flex flex-col divide-y">
                                        {mapLocations.map(location => (
                                            <div 
                                                key={location.id}
                                                className="p-3 flex items-center justify-between hover:bg-muted/50"
                                            >
                                                <div 
                                                    className="flex-1 cursor-pointer" 
                                                    onClick={() => handleGoToLocation(location.id)}
                                                >
                                                    <p className="font-medium">{location.name}</p>
                                                    <p className="text-sm text-muted-foreground truncate">
                                                        {location.address}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleGoToLocation(location.id)}
                                                        title="Go to location"
                                                    >
                                                        <MapPin className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleRemoveLocation(location.id)}
                                                        title="Remove from map"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-full text-muted-foreground">
                                        {locationFilter ? 
                                            "No locations match your filter" : 
                                            "No locations in this map yet"}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <DialogFooter className="flex justify-between">
                        <Button 
                            variant="destructive" 
                            onClick={() => setDeleteDialogOpen(true)}
                            disabled={operationLoading === 'pending' || deleting}
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Map
                        </Button>
                        <Button variant="outline" onClick={onClose}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center">
                            <AlertTriangle className="h-5 w-5 text-destructive mr-2" />
                            Delete Map
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this map? This action cannot be undone
                            and all locations saved to this map will be removed from it.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDeleteMap}
                            disabled={deleting}
                            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                        >
                            {deleting ? 'Deleting...' : 'Yes, Delete Map'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default MapEditModal; 